import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import { Button, Card, Divider, Icon, Layout, Text } from '@loadsmart/miranda-react'

import { openFile } from 'core/utils/browser'

import { useConfigContext } from 'config/ui/ConfigContext'

import { DocumentType } from '../../domain/Document'
import { isLTLorVLTL, type Shipment } from '../../domain/Shipment'
import { Attachments } from '../Attachments'
import { useGetInspectionCertificates } from '../hooks/useGetInspectionCertificates'
import { useGetInvoices } from '../hooks/useGetInvoices'
import { useGetPOD } from '../hooks/useGetPOD'
import { useGetWeightCertificates } from '../hooks/useGetWeightCertificates'
import { useShipment } from '../hooks/useShipment'
import { CapitalizedTag } from '../ShipmentInfo.styles'
import { DocumentItem } from './DocumentItem'
import { DocumentSection } from './DocumentSection'
import UploadDocumentDialog from './UploadDocumentDialog'

export type DocumentCardProps = Readonly<{
  shipment: Shipment
}>

export function DocumentCard({ shipment }: DocumentCardProps) {
  const { fetchBOL, fetchRatecon, downloadingBOL, downloadingRatecon } = useShipment()
  const shipmentUUID = shipment.uuid
  const { invoices, fetchInvoices } = useGetInvoices(shipmentUUID)
  const { podList, fetchPOD } = useGetPOD(shipmentUUID)
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false)

  const { weightCertificates, fetchWeightCertificates } = useGetWeightCertificates(
    shipmentUUID,
    isLTLorVLTL(shipment)
  )
  const { inspectionCertificates, fetchInspectionCertificates } = useGetInspectionCertificates(
    shipmentUUID,
    isLTLorVLTL(shipment)
  )

  const handleDocumentDialogOpen = useCallback(() => {
    setDocumentDialogOpen(!documentDialogOpen)
  }, [documentDialogOpen])

  const configs = useConfigContext()
  const enableEmbeddedAudit = configs.enableEmbeddedAudit.toBoolean()
  const hasPendingDocuments = invoices.length === 0 || podList?.length === 0

  function handleViewInvoice(fileURL: string) {
    openFile(fileURL)
  }

  function handleViewPOD(fileURL: string) {
    openFile(fileURL)
  }

  function handleViewWeightCertificate(fileURL: string) {
    openFile(fileURL)
  }

  function handleViewInspectionCertificate(fileURL: string) {
    openFile(fileURL)
  }

  const viewBOL = useCallback(async () => {
    try {
      const data = await fetchBOL(shipmentUUID)

      openFile(data)
    } catch {
      toast.error('Error downloading BOL. Please try again')
    }
  }, [shipmentUUID, fetchBOL])

  const viewRatecon = useCallback(async () => {
    try {
      const data = await fetchRatecon(shipmentUUID)

      openFile(data)
    } catch {
      toast.error('Error downloading Ratecon. Please try again')
    }
  }, [shipmentUUID, fetchRatecon])

  const onUploadDocument = useCallback(
    async documentType => {
      switch (documentType) {
        case DocumentType.POD:
          fetchPOD(shipmentUUID)
          break
        case DocumentType.Invoice:
          fetchInvoices(shipmentUUID)
          break
        case DocumentType.WeightCertificate:
          fetchWeightCertificates(shipmentUUID)
          break
        case DocumentType.InspectionCertificate:
          fetchInspectionCertificates(shipmentUUID)
          break
      }
    },
    [fetchPOD, shipmentUUID, fetchInvoices, fetchWeightCertificates, fetchInspectionCertificates]
  )

  return (
    <>
      <Card data-testid="document-card" collapsible>
        <Card.Title>
          <Layout.Group>
            <Text variant="heading-sm-bold">Documents</Text>
            {hasPendingDocuments && (
              <CapitalizedTag variant="warning">Pending Documents</CapitalizedTag>
            )}
          </Layout.Group>
        </Card.Title>
        <Card.Divider />
        <Card.Body>
          <Layout.Stack>
            <DocumentSection name="invoices">
              {invoices.map(invoice => (
                <DocumentItem
                  key={invoice.createdAt}
                  title={invoice.total}
                  createdAt={invoice.createdAt}
                  type={DocumentType.Invoice}
                  onClick={() => handleViewInvoice(invoice.url)}
                  status={invoice.status}
                  auditEnabled={enableEmbeddedAudit}
                />
              ))}
            </DocumentSection>
            <Divider />
            <DocumentSection name="rate confirmation">
              <DocumentItem
                title={shipment?.spotQuote?.bookedRate?.flatRate}
                type={DocumentType.RateConfirmation}
                onClick={viewRatecon}
                loading={downloadingRatecon}
                auditEnabled={enableEmbeddedAudit}
              />
            </DocumentSection>
            <Divider />
            <DocumentSection name="bol">
              <DocumentItem
                title={shipment?.bolNumber}
                type={DocumentType.BOL}
                onClick={viewBOL}
                loading={downloadingBOL}
                auditEnabled={enableEmbeddedAudit}
              />
            </DocumentSection>
            <Divider />
            {shipment.spotQuote?.bookedRate && (
              <Attachments
                rateUuid={shipment.spotQuote.bookedRate.uuid}
                attachments={shipment.spotQuote.attachments}
                quoteUuid={shipment.spotQuote.uuid}
              />
            )}
            {shipment.spotQuote && shipment.spotQuote.attachments.length > 0 && <Divider />}
            <DocumentSection name="pod">
              {podList.map(pod => (
                <DocumentItem
                  key={pod.createdAt}
                  title={pod.name}
                  createdAt={pod.createdAt}
                  type={DocumentType.POD}
                  onClick={() => handleViewPOD(pod.url)}
                  auditEnabled={enableEmbeddedAudit}
                />
              ))}
            </DocumentSection>
            {isLTLorVLTL(shipment) && (
              <>
                <Divider />
                <DocumentSection name="weigth certificate">
                  {weightCertificates?.map(weightCertificate => (
                    <DocumentItem
                      key={weightCertificate.createdAt}
                      title={weightCertificate.name}
                      createdAt={weightCertificate.createdAt}
                      type={DocumentType.WeightCertificate}
                      onClick={() => handleViewWeightCertificate(weightCertificate.url)}
                      auditEnabled={enableEmbeddedAudit}
                    />
                  ))}
                </DocumentSection>
                <Divider />
                <DocumentSection name="inspection certificate">
                  {inspectionCertificates?.map(inspectionCertificate => (
                    <DocumentItem
                      key={inspectionCertificate.createdAt}
                      title={inspectionCertificate.name}
                      createdAt={inspectionCertificate.createdAt}
                      type={DocumentType.InspectionCertificate}
                      onClick={() => handleViewInspectionCertificate(inspectionCertificate.url)}
                      auditEnabled={enableEmbeddedAudit}
                    />
                  ))}
                </DocumentSection>
              </>
            )}
          </Layout.Stack>
        </Card.Body>
        <Card.Footer align="flex-start">
          <Button
            size="small"
            leading={<Icon name="upload" size="small" />}
            onClick={handleDocumentDialogOpen}
            data-testid="upload-document-button"
          >
            Upload
          </Button>
        </Card.Footer>
      </Card>
      <UploadDocumentDialog
        shipmentUUID={shipmentUUID}
        isLTLorVLTL={isLTLorVLTL(shipment)}
        open={documentDialogOpen}
        hide={handleDocumentDialogOpen}
        onUploadDocument={onUploadDocument}
      />
    </>
  )
}
